import React, { useEffect, useState } from 'react';
import { Button, H5, Intent, RadioGroup, Radio, Callout } from '@blueprintjs/core';
import withAncestry from 'ecommerce/hoc/withAncestry';
import styled from 'styled-components';
import uuid from 'utils/uuid';
import uniq from 'utils/uniq';
import { parseDate } from 'utils/format';

import useEditorContext from '../../../context/editorContext';
import useEditorItemContext from '../../../context/editorItemContext';
import familysearchApi from '../../../api/familysearch';
import FamilyTreeSelect from '../../../components/ancestry/familyTreeSelect';
import PersonSuggest from '../../../components/ancestry/personSuggest';
import NumberOfGenerationsSelect from '../../../components/ancestry/numberOfGenerationsSelect';
import EventListCard from '../../components/eventListCard';
import EventFormControl from '../../components/eventFormControl';

const StyledRadioGroup = styled(RadioGroup)`
  margin-left: 10px;

  .bp5-control {
    margin-bottom: 0;
  }
`;

// TODO this is weird - we should use standard param - direction
const TREE_TYPE = {
  ancestors: 'Ancestors',
  descendants: 'Descendants',
};

const NUMBER_OF_GENERATIONS = [1, 2, 3, 4, 5];

const FamilysearchPanel = ({ ancestryAuth }) => {
  const { item, template } = useEditorItemContext();
  const editorContext = useEditorContext();

  const [error, setError] = useState();

  const [trees, setTrees] = useState();
  const [selectedTree, setSelectedTree] = useState();

  const [selectedPerson, setSelectedPerson] = useState();

  const [syncPeoplePercent, setSyncPeoplePercent] = useState(null);

  const [events, setEvents] = useState();

  const [selectedNumberOfGenerations, setSelectedNumberOfGenerations] = useState(3);
  const [treeType, setTreeType] = useState(Object.keys(TREE_TYPE)[0]);

  useEffect(() => {
    (async () => {
      const { data, error: treesError } = await familysearchApi(editorContext.accessToken).getTrees();

      if (treesError) {
        setError(treesError);
        return;
      }

      setTrees(data);
      // When user has only one tree, select it automatically
      if (data.length === 1) {
        setSelectedTree(data[0]);
      }
    })();
  }, []);

  const handleLoadPeople = async () => {
    setSyncPeoplePercent(0);
    const api = familysearchApi(editorContext.accessToken, ancestryAuth);

    const { data: personIds, error: personTreeError } = await api.getPersonTree(
      selectedPerson.id,
      selectedNumberOfGenerations,
      treeType,
    );

    setError(personTreeError);

    if (!personTreeError) {
      editorContext.setAdditionalInfo(item.id, {
        ...item.additionalInfo,
        treeId: selectedTree.id,
      });

      const { data: eventsData, error: eventsError } = await api.getEvents(
        selectedTree.id,
        uniq(personIds.map(({ gid }) => gid)),
        parseDate(item.additionalInfo.initialMonth, 'YYYY-MM'),
      );

      setError(eventsError);

      if (eventsData) {
        setEvents(eventsData);
      }
    }

    setSyncPeoplePercent(null);
  };

  const handleOnAddEvents = selectedEvents => {
    editorContext.addEvents(
      item.id,
      selectedEvents.map(event => ({
        key: uuid(),
        date: event.date,
        description: event.description,
        type: 'familysearch',
      })),
    );
  };

  if (!trees) {
    return null;
  }

  if (error) {
    return (
      <>
        <Callout intent={Intent.DANGER}>
          Oh no! We’re sorry, something has gone wrong. We’re working to fix the issue. In the
          meantime, you could try refreshing your page.
        </Callout>
      </>
    );
  }

  if (trees.length === 0) {
    return <H5>Your FamilySearch account has no family tree.</H5>;
  }

  return (
    <>
      <H5 className="mb-3">Import events from your FamilySearch.com family tree</H5>

      <div className={trees.length <= 1 && "d-none"}>
        <EventFormControl label="Family tree">
          <FamilyTreeSelect
            trees={trees}
            fill
            selectedTree={selectedTree}
            onChange={setSelectedTree}
          />
        </EventFormControl>
      </div>

      <EventFormControl label="Starting person">
        <PersonSuggest
          type="familysearch"
          treeId={selectedTree?.id}
          disabled={!selectedTree}
          fill
          selectedPerson={selectedPerson}
          onChange={setSelectedPerson}
        />
      </EventFormControl>

      <EventFormControl label="Number of generations" className="mw-800">
        <NumberOfGenerationsSelect
          values={NUMBER_OF_GENERATIONS}
          selectedValue={selectedNumberOfGenerations}
          onChange={setSelectedNumberOfGenerations}
        />

        <StyledRadioGroup
          inline
          onChange={e => setTreeType(e.currentTarget.value)}
          selectedValue={treeType}
        >
          {Object.entries(TREE_TYPE).map(([key, label]) => (
            <Radio key={key} label={label} value={key} />
          ))}
        </StyledRadioGroup>
      </EventFormControl>

      <EventFormControl className="mw-800">
        <Button
          intent={Intent.PRIMARY}
          disabled={!selectedPerson}
          onClick={handleLoadPeople}
          loading={syncPeoplePercent !== null}
        >
          Get People
        </Button>
        {syncPeoplePercent !== null && (
          <span className="ml-1">
            {syncPeoplePercent > 0 ? (
              <>
                Gathering data from FamilySearch <strong>({parseInt(syncPeoplePercent, 10)}%)</strong>
              </>
            ) : (
              <>Preparing data from FamilySearch. It should take a while.</>
            )}
          </span>
        )}
      </EventFormControl>

      <EventListCard
        events={events}
        showTypeFilter
        onAddEvents={handleOnAddEvents}
        dateMask="MMM D, YYYY"
      />
    </>
  );
};

export default withAncestry(FamilysearchPanel, "familysearch");
